import React, { useContext, useEffect, useMemo, useState } from "react";

import _ from "lodash";
import cx from "classnames";

import TextareaAutosize from "react-autosize-textarea";
import { BannerView } from "src/components/banner";

import Slide from "../../../components/slide";
import { Simulation } from "src/types/coaching/simulation";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { Banner } from "src/types/common";
import { updateSimulationProperty } from "src/redux/actions/coaching/simulation";
import { EditCourseThumbnail } from "src/pages/coaching/manage/courses/edit/thumbnail";
import { SelectSkillsDialog } from "src/pages/coaching/manage/courses/edit/components/selectSkills";
import { CourseLearners } from "src/pages/coaching/manage/courses/edit/components/learners";
import { DIFFICULTY_CHOICES, DURATION_CHOICES } from "src/constants/simulation";
import { ErrorMessage } from "../../../components/error";
import { SimulationContext } from "../../context";

import styles from "./callSettings.module.scss";

interface CallSettingsProps {
  simulation: Simulation;
}

export const CallSettings: React.FC<CallSettingsProps> = ({ simulation }) => {
  const dispatch = useDispatch();
  const simulationContext = useContext(SimulationContext);

  const id = simulation.id;

  const name = useMemo(() => {
    return _.get(simulation, "name") || "";
  }, [simulation]);

  const description = useMemo(() => {
    return _.get(simulation, "description") || "";
  }, [simulation]);

  const banner = useMemo(() => {
    return _.get(simulation, "banner");
  }, [simulation]);

  const duration = useMemo(() => {
    return _.get(simulation, "duration");
  }, [simulation]);

  const difficulty = useMemo(() => {
    return _.get(simulation, "difficulty");
  }, [simulation]);

  const nameInputRef = React.useRef<HTMLTextAreaElement>(null);
  const descriptionInputRef = React.useRef<any>(null);

  const nameError = useSelector((state: any) => {
    const errors = _.get(state, `coaching.simulations.edit.errors`) || [];
    return _.find(errors, { type: "call-settings", property: "name" });
  });

  const bannerError = useSelector((state: any) => {
    const errors = _.get(state, `coaching.simulations.edit.errors`) || [];
    return _.find(errors, { type: "call-settings", property: "banner" });
  });

  const descriptionError = useSelector((state: any) => {
    const errors = _.get(state, `coaching.simulations.edit.errors`) || [];
    return _.find(errors, { type: "call-settings", property: "description" });
  });

  const [editThumbnail, setEditThumbnail] = useState(false);
  const [showSkillSlider, setShowSkillSlider] = useState(false);

  const handlePropertyChange = (property: string, value: any) => {
    dispatch(updateSimulationProperty({
      simulation: simulation.id!,
      property,
      value,
    }),
    );
  };

  const handleNameChange = (e: any) => {
    let input = e.target.value;
    input = input.replace(/\n/g, "");
    handlePropertyChange("name", input);
  };

  const handleDescriptionChange = (e: any) => {
    console.log("Description changed", e.target.value);
    const content = e.target.value;
    handlePropertyChange("description", content);
  };

  const handleThumbnailChange = (banner: Banner) => {
    handlePropertyChange("banner", banner);
    setEditThumbnail(false);
  };

  const handleDurationChange = (duration: any) => {
    handlePropertyChange("duration", duration);
  };

  const handleDifficultyChange = (difficulty: any) => {
    handlePropertyChange("difficulty", difficulty);
  };

  const handleAddSkillsFinish = (skills) => {
    handlePropertyChange("skills", skills);
  };

  const onSkillClicked = () => {
    setShowSkillSlider(true);
  }

  const handleSkillRemove = (skill: any) => {
    const items = simulation.skills?.filter((item: any) => item.id !== skill.id);
    handlePropertyChange("skills", items);
  };

  useEffect(() => {
    return () => {
      console.log("Unmounting CallSettings");
    }
  }, []);

  useEffect(() => {
    if (simulationContext.validationError) {
      const type = simulationContext.validationError.type;
      const property = simulationContext.validationError.property;

      switch (type) {
        case "call-settings": {
          if (property === "name") {
            setTimeout(() => {
              nameInputRef.current?.focus();
            }, 1000);
          } else if (property === "description") {
            setTimeout(() => {
              descriptionInputRef.current?.focus();
            }, 1000);
          } else if (property === "skills") {
            setShowSkillSlider(true);
          }

          simulationContext.gotoError?.(undefined);

          break;
        }

        default: {
          break;
        }
      }
    }
  }, [simulationContext.validationError]);


  return (
    <div className={styles.container}>
      <div className={styles.left}>
        <div className={styles.details}>
          <div className={cx(styles.field, styles.name)}>
            <TextareaAutosize
              className={styles.input}
              ref={nameInputRef}
              placeholder="Scenario name goes here..."
              rows={1}
              maxRows={10}
              value={name}
              onChange={handleNameChange}
              onPointerEnterCapture={() => { }}
              onPointerLeaveCapture={() => { }}
            />
            <ErrorMessage error={nameError} />
          </div>

          <div
            className={cx(styles.field, styles.thumbnail)}
            onClick={() => {
              setEditThumbnail(true);
            }}
          >
            <div className={styles.inner}>
              <div className={styles.overlay}>
                <i className="paperflite-edit"></i>
              </div>
              <BannerView
                banner={banner as any}
                defaultBanner="/images/assets/default_asset.jpg"
              />
            </div>
            <ErrorMessage error={bannerError} />
          </div>

          <div className={cx(styles.field, styles.description)}>
            <div className={styles.label}>Description</div>
            <TextareaAutosize
              className={styles.descriptionInput}
              rows={1}
              value={description}
              placeholder={
                'Describe the scenario to set the stage for your reps.\nInclude helpful context and key pointers to guide them through the simulation.\nMake it clear so they can confidently navigate the experience!...'
              }
              spellCheck={true}
              onChange={handleDescriptionChange}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
            <ErrorMessage error={descriptionError} />
          </div>

          <div className={cx(styles.field, styles.duration)}>
            <div className={styles.label}>Call Duration</div>
            <p className={styles.subtext}>
              Set a time limit: short calls for specific skills, longer ones for full interactions
            </p>
            <Slide.Choice value={duration} options={DURATION_CHOICES} onChange={handleDurationChange} />
          </div>

          <div className={cx(styles.field, styles.difficulty)}>
            <div className={styles.label}>Challenge Level</div>
            <p className={styles.subtext}>
              Set the challenge level to match the skills you want reps to master
            </p>
            <Slide.Choice value={difficulty} options={DIFFICULTY_CHOICES} onChange={handleDifficultyChange} />
          </div>
        </div>
      </div>
      <div className={styles.right}>
        <p className={styles.heading}>
          Skills in focus ({0})
        </p>
        <div className={styles.skills}>
          <div
            className={styles.addSkillButton}
            onClick={() => onSkillClicked()}
          >
            <i className={cx(styles.icon, "paperflite-add")}></i>
          </div>

          {(simulation.skills || []).map((skill: any) => {
            return (
              <div key={skill.id} className={styles.skill}>
                <div className={styles.name}>{skill.name}</div>
                <i
                  className={cx("paperflite-close", styles.closeIcon)}
                  onClick={() => handleSkillRemove(skill)}
                ></i>
              </div>
            );
          })}
        </div>
        <div className={styles.courseLearners}>
          <CourseLearners id={simulation.id!} />
        </div>
      </div>

      {editThumbnail && (
        <EditCourseThumbnail
          show={editThumbnail}
          banner={simulation?.banner || null}
          onFinish={handleThumbnailChange}
          onClose={() => setEditThumbnail(false)}
        />
      )}

      {showSkillSlider && (
        <SelectSkillsDialog
          show={showSkillSlider}
          selected={simulation.skills || []}
          onFinish={handleAddSkillsFinish}
          onClose={() => setShowSkillSlider(false)}
        />
      )}
    </div>
  )
}
